import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Check,
  Info,
  Layers,
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  Plus,
  Search,
  Shield,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Segmented } from '@/components/page';
import {
  CardHead,
  ConfirmDialog,
  ChoiceCard,
  EmptyState,
  FieldError,
  GrantPill,
  ListFooter,
  PERM_RE,
  ROLE_NAME_RE,
  Tag,
  ago,
  daysUntil,
  fmtDate,
  fmtDateTime,
  grantStateOf,
  initialsOf,
  type GrantState,
} from '@/components/products/shared';
import {
  useCreateProductRole,
  useDeleteProductRole,
  useGrantProductAccess,
  useProductAccess,
  useProductRoles,
  useRevokeProductAccess,
  useSetProductAccessActive,
  useToast,
  useUpdateProductAccess,
  useUpdateProductRole,
  useUsers,
} from '@/hooks';
import type { AdminUser } from '@/api/users';
import type { ProductRole } from '@/api/products';
import type { Product, ProductAccess } from '@/types';

const PAGE_SIZE = 10;

function parseList(value: string) {
  return Array.from(
    new Set(
      value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function errMsg(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

const accessName = (a: ProductAccess) => a.user_name || a.user_email || 'Unknown user';

/** Run an effect whenever a "signal" counter increments (skips the initial value). */
function useSignal(signal: number | undefined, fn: () => void) {
  const first = useRef(signal ?? 0);
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (signal === undefined || signal === first.current) return;
    first.current = signal;
    ref.current();
  }, [signal]);
}

/* ════════════════════════════════════════════════════════
   Roles
   ════════════════════════════════════════════════════════ */

export function ProductRolesPanel({
  productId,
  product,
  signal,
}: {
  productId: string;
  product: Product;
  /** Increment to open the "New role" dialog. */
  signal?: number;
}) {
  const { toast } = useToast();
  const { data: roles = [], isLoading, isError, refetch } = useProductRoles(productId);
  const { data: grants = [] } = useProductAccess(productId);
  const createRole = useCreateProductRole();
  const updateRole = useUpdateProductRole();
  const deleteRole = useDeleteProductRole();

  const [editing, setEditing] = useState<ProductRole | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<ProductRole | null>(null);

  const openNew = () => {
    setEditing(null);
    setOpen(true);
  };
  useSignal(signal, openNew);

  const defaultRole = product.settings?.default_role;
  const known = useMemo(() => [...new Set(roles.flatMap((r) => r.permissions))], [roles]);
  const using = (name: string) => grants.filter((g) => g.roles.includes(name)).length;

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteRole.mutateAsync({ productId, name: deleting.name });
      toast({ title: 'Role deleted', description: deleting.name });
      setDeleting(null);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Role delete failed',
        description: errMsg(error, 'Failed to delete role'),
      });
    }
  };

  const newButton = (
    <Button size="sm" variant="outline" onClick={openNew}>
      <Plus className="h-4 w-4" />
      New role
    </Button>
  );

  return (
    <>
      <Card className="overflow-hidden">
        <CardHead
          title="Roles"
          sub="Roles bundle permissions. Grants assign roles to people."
          action={roles.length > 0 ? newButton : undefined}
        />
        {isLoading ? (
          <div className="divide-y divide-border" aria-busy="true">
            {[0, 1].map((i) => (
              <div key={i} className="h-20 animate-pulse bg-foreground/[0.03]" />
            ))}
          </div>
        ) : isError ? (
          <EmptyState
            icon={Layers}
            title="Couldn’t load roles"
            action={
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : roles.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No roles yet"
            body={
              <>
                Without roles, grants only allow sign-in. Add roles like <code>viewer</code> or{' '}
                <code>admin</code> and give them permissions.
              </>
            }
            action={newButton}
          />
        ) : (
          <div className="divide-y divide-border">
            {roles.map((r) => {
              const n = using(r.name);
              return (
                <div
                  key={r.name}
                  className="grid gap-3 px-[18px] py-3.5 md:grid-cols-[minmax(0,220px)_minmax(0,1fr)_auto] md:gap-4"
                >
                  <div className="grid min-w-0 gap-0.5">
                    <code className="truncate text-[13px] font-semibold">{r.name}</code>
                    <span className="text-xs text-muted-foreground">
                      {r.description || 'No description'}
                    </span>
                    <span className="font-mono text-xs tabular-nums text-muted-foreground">
                      {n} {n === 1 ? 'grant' : 'grants'}
                      {defaultRole === r.name ? ' · default role' : ''}
                    </span>
                  </div>
                  <div>
                    {r.permissions.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {r.permissions.map((p) => (
                          <Tag key={p}>{p}</Tag>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        No permissions — the role name alone is emitted.
                      </span>
                    )}
                  </div>
                  <div className="flex items-start gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit role ${r.name}`}
                      onClick={() => {
                        setEditing(r);
                        setOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete role ${r.name}`}
                      onClick={() => setDeleting(r)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <RoleDialog
        open={open}
        onOpenChange={setOpen}
        role={editing}
        existing={roles.map((r) => r.name)}
        known={known}
        pending={createRole.isPending || updateRole.isPending}
        onSave={async (data) => {
          try {
            if (editing) {
              await updateRole.mutateAsync({ productId, currentName: editing.name, data });
              toast({ title: 'Role saved', description: data.name });
            } else {
              await createRole.mutateAsync({
                productId,
                data: { name: data.name, description: data.description, permissions: data.permissions },
              });
              toast({
                title: 'Role created',
                description: `${data.name} · ${data.permissions.length} ${
                  data.permissions.length === 1 ? 'permission' : 'permissions'
                }`,
              });
            }
            setOpen(false);
          } catch (error) {
            toast({
              variant: 'destructive',
              title: 'Role save failed',
              description: errMsg(error, 'Failed to save role'),
            });
          }
        }}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete role ${deleting?.name ?? ''}?`}
        body={
          deleting && using(deleting.name) > 0
            ? `${using(deleting.name)} ${
                using(deleting.name) === 1 ? 'grant uses' : 'grants use'
              } this role. It will be removed from them, and their next tokens won’t include it.`
            : 'No grants use this role.'
        }
        confirmLabel="Delete role"
        pendingLabel="Deleting…"
        danger
        pending={deleteRole.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}

function RoleDialog({
  open,
  onOpenChange,
  role,
  existing,
  known,
  pending,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: ProductRole | null;
  existing: string[];
  known: string[];
  pending: boolean;
  onSave: (data: { name: string; description: string; permissions: string[] }) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [perms, setPerms] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [nameErr, setNameErr] = useState('');
  const [permErr, setPermErr] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    setPerms(role?.permissions ?? []);
    setDraft('');
    setNameErr('');
    setPermErr('');
  }, [open, role]);

  const addPerms = (raw: string) => {
    const parts = raw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const bad = parts.find((p) => !PERM_RE.test(p));
    if (bad) {
      setPermErr(`“${bad}” isn’t resource:action.`);
      return false;
    }
    setPerms((prev) => [...prev, ...parts.filter((p) => !prev.includes(p))]);
    setPermErr('');
    setDraft('');
    return true;
  };

  const checkName = () => {
    const v = name.trim();
    let msg = '';
    if (!v) msg = 'Enter a role name.';
    else if (!ROLE_NAME_RE.test(v)) msg = 'Use lowercase letters, numbers, hyphens or underscores.';
    else if (existing.includes(v) && v !== role?.name) msg = 'This app already has a role with that name.';
    setNameErr(msg);
    return !msg;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (draft.trim() && !addPerms(draft)) return inputRef.current?.focus();
    if (!checkName()) return;
    const finalPerms = draft.trim()
      ? Array.from(new Set([...perms, ...draft.split(/[\s,]+/).filter(Boolean)]))
      : perms;
    onSave({ name: name.trim(), description: description.trim(), permissions: finalPerms });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={submit} noValidate className="space-y-5">
          <DialogHeader>
            <DialogTitle>{role ? `Edit role ${role.name}` : 'New role'}</DialogTitle>
            <DialogDescription>
              Permissions use <code>resource:action</code>, for example <code>invoices:read</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="r-name">Name</Label>
            <Input
              id="r-name"
              className="font-mono"
              required
              maxLength={40}
              placeholder="viewer"
              spellCheck={false}
              autoCapitalize="off"
              autoComplete="off"
              value={name}
              aria-invalid={!!nameErr}
              onChange={(e) => setName(e.target.value)}
              onBlur={checkName}
            />
            <FieldError>{nameErr}</FieldError>
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, hyphens and underscores. Emitted as-is in tokens.
              {role && ' Renaming updates every grant that uses it.'}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="r-desc">
              Description
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">optional</span>
            </Label>
            <Input
              id="r-desc"
              maxLength={120}
              placeholder="What this role can do"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="r-perm">Permissions</Label>
            <div
              className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-card px-2 py-1.5 focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/20"
              onClick={() => inputRef.current?.focus()}
            >
              {perms.map((p) => (
                <span
                  key={p}
                  className="inline-flex h-[22px] items-center gap-1 rounded-[5px] border border-border bg-card pl-[7px] pr-1 font-mono text-[11.5px]"
                >
                  {p}
                  <button
                    type="button"
                    aria-label={`Remove ${p}`}
                    className="rounded text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPerms((prev) => prev.filter((x) => x !== p));
                      inputRef.current?.focus();
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                id="r-perm"
                ref={inputRef}
                list="r-known"
                autoComplete="off"
                spellCheck={false}
                placeholder={perms.length ? '' : 'invoices:read'}
                aria-describedby="r-perm-hint"
                value={draft}
                className="min-w-[8ch] flex-1 bg-transparent font-mono text-[12.5px] outline-none placeholder:text-muted-foreground"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && draft.trim()) {
                    e.preventDefault();
                    addPerms(draft);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                  } else if (e.key === 'Backspace' && !draft && perms.length) {
                    setPerms((prev) => prev.slice(0, -1));
                  }
                }}
                onPaste={(e) => {
                  const t = e.clipboardData.getData('text');
                  if (/[\s,]/.test(t)) {
                    e.preventDefault();
                    addPerms(t);
                  }
                }}
                onBlur={() => draft.trim() && addPerms(draft)}
              />
            </div>
            <datalist id="r-known">
              {known.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
            <FieldError>{permErr}</FieldError>
            <p id="r-perm-hint" className="text-xs text-muted-foreground">
              Press Enter or comma to add. Backspace removes the last one.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : role ? 'Save role' : 'Create role'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ════════════════════════════════════════════════════════
   Access grants
   ════════════════════════════════════════════════════════ */

type StateFilter = 'all' | GrantState;

interface PolicyDraft {
  roles: string[];
  custom_roles: string;
  permissions: string;
  expires: string; // yyyy-mm-dd
}

const emptyDraft = (): PolicyDraft => ({ roles: [], custom_roles: '', permissions: '', expires: '' });

const todayStr = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const toDateInput = (value: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const expiryIso = (date: string) => (date ? new Date(`${date}T23:59:59`).toISOString() : undefined);

export function ProductAccessPanel({
  productId,
  product,
  signal,
  onDefineRole,
}: {
  productId: string;
  product: Product;
  /** Increment to open the "Grant access" dialog. */
  signal?: number;
  onDefineRole?: () => void;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: access = [], isLoading, isError, refetch } = useProductAccess(productId);
  const { data: roles = [] } = useProductRoles(productId);
  const { data: users = [], isLoading: usersLoading } = useUsers();
  const grantAccess = useGrantProductAccess();
  const updateAccess = useUpdateProductAccess();
  const revokeAccess = useRevokeProductAccess();
  const setActive = useSetProductAccessActive();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StateFilter>('all');
  const [page, setPage] = useState(1);
  const [grantOpen, setGrantOpen] = useState(false);
  const [editing, setEditing] = useState<ProductAccess | null>(null);
  const [suspending, setSuspending] = useState<ProductAccess | null>(null);
  const [revoking, setRevoking] = useState<ProductAccess | null>(null);

  useSignal(signal, () => setGrantOpen(true));

  const userById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const model = product.settings?.access_model ?? 'gated';

  const withState = useMemo(
    () => access.map((a) => ({ a, state: grantStateOf(a) })),
    [access]
  );
  const count = (s: StateFilter) =>
    s === 'all' ? withState.length : withState.filter((x) => x.state === s).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return withState
      .filter(
        ({ a, state }) =>
          (filter === 'all' || state === filter) &&
          (!q ||
            [a.user_name, a.user_email, ...a.roles, ...a.permissions]
              .filter(Boolean)
              .some((v) => v!.toLowerCase().includes(q)))
      )
      .sort((x, y) => accessName(x.a).localeCompare(accessName(y.a)));
  }, [withState, filter, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const rows = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const grantButton = (
    <Button size="sm" variant="outline" onClick={() => setGrantOpen(true)}>
      <UserPlus className="h-4 w-4" />
      Grant access
    </Button>
  );

  const handleSuspend = async () => {
    if (!suspending) return;
    try {
      await setActive.mutateAsync({ productId, userId: suspending.user_id, isActive: false });
      toast({ title: 'Access suspended', description: accessName(suspending) });
      setSuspending(null);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Suspend failed', description: errMsg(error, 'Failed to suspend access') });
    }
  };

  const handleResume = async (a: ProductAccess) => {
    try {
      await setActive.mutateAsync({ productId, userId: a.user_id, isActive: true });
      toast({ title: 'Access resumed', description: `${accessName(a)} can sign in again.` });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Resume failed', description: errMsg(error, 'Failed to resume access') });
    }
  };

  const handleRevoke = async () => {
    if (!revoking) return;
    try {
      await revokeAccess.mutateAsync({ productId, userId: revoking.user_id });
      toast({
        title: 'Access revoked',
        description: `${accessName(revoking)} no longer has a grant for ${product.name}.`,
      });
      setRevoking(null);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Revoke failed', description: errMsg(error, 'Failed to revoke access') });
    }
  };

  return (
    <div className="space-y-4">
      {model === 'open' && (
        <Note title="Open app">
          Any signed-in user can sign in. Grants here only add roles and permissions.
        </Note>
      )}
      {model === 'org_members' && (
        <Note title="Organization-member access">
          Members of the organization sign in{' '}
          {product.settings?.default_role ? (
            <>
              with <code>{product.settings.default_role}</code>
            </>
          ) : (
            'without a default role'
          )}
          . A grant here overrides their roles.
        </Note>
      )}

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="divide-y divide-border" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 animate-pulse bg-foreground/[0.03]" />
            ))}
          </div>
        ) : isError ? (
          <EmptyState
            icon={Shield}
            title="Couldn’t load access grants"
            action={
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : access.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No one has been granted access"
            body={
              model === 'gated'
                ? 'This app is gated, so nobody can sign in until you grant access.'
                : 'Grant access to give specific people roles in this app.'
            }
            action={grantButton}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 border-b border-border px-[18px] py-3">
              <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  aria-label="Search grants"
                  placeholder="Search name, email or role"
                  autoComplete="off"
                  className="pl-9"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <Segmented<StateFilter>
                value={filter}
                onChange={(v) => {
                  setFilter(v);
                  setPage(1);
                }}
                options={[
                  { value: 'all', label: 'All', count: count('all') },
                  { value: 'active', label: 'Active', count: count('active') },
                  { value: 'expiring', label: 'Expiring', count: count('expiring') },
                  { value: 'suspended', label: 'Suspended', count: count('suspended') },
                  { value: 'expired', label: 'Expired', count: count('expired') },
                ]}
              />
              <span className="flex-1" />
              {grantButton}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="hidden md:table-cell">Expires</TableHead>
                  <TableHead className="hidden md:table-cell">Granted</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={6}>
                      <EmptyState
                        icon={Search}
                        title="No grants match"
                        body="Try another name or state."
                        action={
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSearch('');
                              setFilter('all');
                            }}
                          >
                            Clear filters
                          </Button>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map(({ a, state }) => {
                    const user = userById.get(a.user_id);
                    const days = daysUntil(a.expires_at);
                    const dim = state === 'suspended' || state === 'expired';
                    return (
                      <TableRow
                        key={a.id}
                        className={`cursor-pointer ${dim ? 'opacity-60' : ''}`}
                        onClick={() => navigate(`/users/${a.user_id}`)}
                      >
                        <TableCell>
                          <Link
                            to={`/users/${a.user_id}`}
                            className="flex min-w-0 items-center gap-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <UserAvatar user={user} name={a.user_name} email={a.user_email} />
                            <span className="min-w-0">
                              <b className="block max-w-[240px] truncate font-semibold" title={accessName(a)}>
                                {accessName(a)}
                              </b>
                              {a.user_name && a.user_email && (
                                <span className="block max-w-[240px] truncate text-xs text-muted-foreground">
                                  {a.user_email}
                                </span>
                              )}
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell>
                          {a.roles.length || a.permissions.length ? (
                            <div className="flex flex-wrap gap-1.5">
                              {a.roles.map((r) => (
                                <Tag key={r}>{r}</Tag>
                              ))}
                              {a.permissions.map((p) => (
                                <Tag key={p} solid title="Extra permission">
                                  +{p}
                                </Tag>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">Sign-in only</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <GrantPill state={state} />
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {a.expires_at ? (
                            <span className="grid whitespace-nowrap">
                              <span className="font-mono text-xs tabular-nums">{fmtDate(a.expires_at)}</span>
                              <span className="text-xs text-muted-foreground">
                                {days !== null && days > 0 ? `in ${days}d` : `${Math.abs(days ?? 0)}d ago`}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Never</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <span
                            className="whitespace-nowrap font-mono text-xs tabular-nums"
                            title={fmtDateTime(a.granted_at)}
                          >
                            {ago(a.granted_at)}
                          </span>
                        </TableCell>
                        <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actions for ${accessName(a)}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setEditing(a)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit roles &amp; expiry
                              </DropdownMenuItem>
                              {a.is_active ? (
                                <DropdownMenuItem onClick={() => setSuspending(a)}>
                                  <Pause className="mr-2 h-4 w-4" />
                                  Suspend access
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleResume(a)}>
                                  <Play className="mr-2 h-4 w-4" />
                                  Resume access
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setRevoking(a)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Revoke access
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <ListFooter
              page={current}
              pageCount={pageCount}
              total={filtered.length}
              pageSize={PAGE_SIZE}
              noun="grants"
              onPage={setPage}
            />
          </>
        )}
      </Card>

      <GrantDialog
        open={grantOpen || !!editing}
        onOpenChange={(o) => {
          if (!o) {
            setGrantOpen(false);
            setEditing(null);
          }
        }}
        product={product}
        roles={roles}
        users={users}
        usersLoading={usersLoading}
        takenIds={new Set(access.map((a) => a.user_id))}
        editing={editing}
        pending={grantAccess.isPending || updateAccess.isPending}
        onDefineRole={
          onDefineRole
            ? () => {
                setGrantOpen(false);
                setEditing(null);
                onDefineRole();
              }
            : undefined
        }
        onSubmit={async ({ userId, roles: picked, permissions, expires }) => {
          try {
            if (editing) {
              await updateAccess.mutateAsync({
                productId,
                userId: editing.user_id,
                data: {
                  roles: picked,
                  permissions,
                  expires_at: expires ? (expiryIso(expires) ?? null) : null,
                },
              });
              toast({
                title: 'Access updated',
                description: `${accessName(editing)}: ${picked.join(', ') || 'sign-in only'}.`,
              });
            } else {
              await grantAccess.mutateAsync({
                productId,
                data: { user_id: userId, roles: picked, permissions, expires_at: expiryIso(expires) },
              });
              const u = userById.get(userId);
              toast({
                title: 'Access granted',
                description: `${u?.name || u?.email || 'The user'} can now use ${product.name}.`,
              });
            }
            setGrantOpen(false);
            setEditing(null);
          } catch (error) {
            toast({
              variant: 'destructive',
              title: editing ? 'Update failed' : 'Grant failed',
              description: errMsg(error, 'Failed to save access'),
            });
          }
        }}
      />

      <ConfirmDialog
        open={!!suspending}
        onOpenChange={(o) => !o && setSuspending(null)}
        title={`Suspend ${suspending ? accessName(suspending) : ''}?`}
        body={`Their grant is kept so you can resume it later, but ${product.name} refuses their sign-ins and token refreshes until then.`}
        confirmLabel="Suspend access"
        pendingLabel="Suspending…"
        pending={setActive.isPending}
        onConfirm={handleSuspend}
      />
      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={`Revoke access for ${revoking ? accessName(revoking) : ''}?`}
        body={`This deletes the grant and its roles (${
          revoking?.roles.join(', ') || 'none'
        }). To give access back you’ll need a new grant.`}
        confirmLabel="Revoke access"
        pendingLabel="Revoking…"
        danger
        pending={revokeAccess.isPending}
        onConfirm={handleRevoke}
      />
    </div>
  );
}

function Note({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 rounded-lg border border-border bg-card p-3.5">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div>
        <b className="text-[13.5px] font-semibold">{title}</b>
        <p className="text-[13px] text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}

function UserAvatar({
  user,
  name,
  email,
}: {
  user?: AdminUser;
  name?: string | null;
  email?: string | null;
}) {
  return (
    <Avatar className="h-8 w-8 shrink-0 border">
      {user?.picture && <AvatarImage src={user.picture} alt="" />}
      <AvatarFallback className="text-xs font-medium">
        {initialsOf(name ?? user?.name, email ?? user?.email)}
      </AvatarFallback>
    </Avatar>
  );
}

function GrantDialog({
  open,
  onOpenChange,
  product,
  roles,
  users,
  usersLoading,
  takenIds,
  editing,
  pending,
  onDefineRole,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product;
  roles: ProductRole[];
  users: AdminUser[];
  usersLoading: boolean;
  takenIds: Set<string>;
  editing: ProductAccess | null;
  pending: boolean;
  onDefineRole?: () => void;
  onSubmit: (v: { userId: string; roles: string[]; permissions: string[]; expires: string }) => void;
}) {
  const [userId, setUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [draft, setDraft] = useState<PolicyDraft>(emptyDraft);
  const [errors, setErrors] = useState<{ user?: string; expires?: string }>({});
  const today = todayStr();

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setUserSearch('');
    if (editing) {
      const catalog = new Set(roles.map((r) => r.name));
      setUserId(editing.user_id);
      setDraft({
        roles: editing.roles.filter((r) => catalog.has(r)),
        custom_roles: editing.roles.filter((r) => !catalog.has(r)).join(', '),
        permissions: editing.permissions.join(', '),
        expires: toDateInput(editing.expires_at),
      });
    } else {
      setUserId('');
      setDraft(emptyDraft());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const candidates = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return users
      .filter((u) => u.isActive && !takenIds.has(u.id))
      .filter((u) => !q || [u.name, u.email].filter(Boolean).some((v) => v!.toLowerCase().includes(q)));
  }, [users, takenIds, userSearch]);
  const poolSize = users.filter((u) => u.isActive && !takenIds.has(u.id)).length;
  const picked = users.find((u) => u.id === userId);

  const inherited = useMemo(
    () => [...new Set(roles.filter((r) => draft.roles.includes(r.name)).flatMap((r) => r.permissions))],
    [roles, draft.roles]
  );

  const toggleRole = (name: string) =>
    setDraft((d) => ({
      ...d,
      roles: d.roles.includes(name) ? d.roles.filter((r) => r !== name) : [...d.roles, name],
    }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!userId) next.user = 'Choose who gets access.';
    if (draft.expires && draft.expires < today) next.expires = 'Pick today or a later date.';
    setErrors(next);
    if (Object.keys(next).length) return;
    onSubmit({
      userId,
      roles: Array.from(new Set([...draft.roles, ...parseList(draft.custom_roles)])),
      permissions: parseList(draft.permissions),
      expires: draft.expires,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={submit} noValidate className="space-y-5">
          <DialogHeader>
            <DialogTitle>
              {editing ? `Edit access for ${editing.user_name || editing.user_email}` : `Grant access to ${product.name}`}
            </DialogTitle>
            <DialogDescription>
              {(product.settings?.access_model ?? 'gated') === 'gated'
                ? 'A grant lets the person sign in. Roles decide what their tokens carry.'
                : 'Roles here are added to the person’s tokens for this app.'}
            </DialogDescription>
          </DialogHeader>

          {!editing && (
            <div className="space-y-2">
              <Label htmlFor="grant-user-search">Person</Label>
              {picked ? (
                <div className="flex items-center gap-3 rounded-lg border border-border bg-foreground/[0.03] p-3">
                  <UserAvatar user={picked} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{picked.name || picked.email}</p>
                    {picked.name && (
                      <p className="truncate text-xs text-muted-foreground">{picked.email}</p>
                    )}
                  </div>
                  <Check className="h-4 w-4 text-success" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setUserId('')}>
                    Change
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="grant-user-search"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by name or email"
                      className="pl-9"
                      aria-invalid={!!errors.user}
                      autoComplete="off"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-border p-1">
                    {usersLoading ? (
                      <p className="p-4 text-center text-sm text-muted-foreground">Loading users…</p>
                    ) : candidates.length === 0 ? (
                      <p className="p-4 text-center text-sm text-muted-foreground">
                        {poolSize === 0
                          ? 'Everyone already has a grant. Edit an existing grant instead.'
                          : 'No users match this search.'}
                      </p>
                    ) : (
                      candidates.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          className="flex min-h-11 w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => {
                            setUserId(u.id);
                            setErrors((er) => ({ ...er, user: undefined }));
                          }}
                        >
                          <UserAvatar user={u} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">{u.name || u.email}</span>
                            <span className="block truncate text-xs text-muted-foreground">{u.email}</span>
                          </span>
                          <Plus className="h-4 w-4 text-muted-foreground" />
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
              <FieldError>{errors.user}</FieldError>
            </div>
          )}
          {editing && (
            <div className="flex items-center gap-3">
              <UserAvatar name={editing.user_name} email={editing.user_email} />
              <span className="min-w-0">
                <b className="block truncate font-semibold">{accessName(editing)}</b>
                {editing.user_name && (
                  <span className="block truncate text-xs text-muted-foreground">{editing.user_email}</span>
                )}
              </span>
            </div>
          )}

          <fieldset className="space-y-2 border-t border-border pt-5">
            <div className="flex items-center justify-between gap-3">
              <legend className="text-[13px] font-medium">Roles</legend>
              {onDefineRole && (
                <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={onDefineRole}>
                  Define a role
                </Button>
              )}
            </div>
            {roles.length === 0 ? (
              <div className="flex gap-3 rounded-lg border border-border p-3.5">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <b className="text-[13.5px] font-semibold">No roles defined</b>
                  <p className="text-[13px] text-muted-foreground">
                    The grant allows sign-in only. Add roles on the Roles tab.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {roles.map((r) => (
                  <ChoiceCard
                    key={r.name}
                    type="checkbox"
                    value={r.name}
                    mono
                    checked={draft.roles.includes(r.name)}
                    onChange={() => toggleRole(r.name)}
                    title={r.name}
                    description={r.description || r.permissions.join(', ') || 'No permissions'}
                  />
                ))}
              </div>
            )}
            {inherited.length > 0 && (
              <div className="rounded-lg bg-foreground/[0.04] p-3">
                <p className="text-xs font-medium text-muted-foreground">Inherited from selected roles</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {inherited.map((p) => (
                    <Tag key={p}>{p}</Tag>
                  ))}
                </div>
              </div>
            )}
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="g-custom">Custom roles</Label>
              <Input
                id="g-custom"
                className="font-mono"
                placeholder="Optional ad-hoc role names"
                value={draft.custom_roles}
                onChange={(e) => setDraft((d) => ({ ...d, custom_roles: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Comma-separated. Catalog roles are preferred.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-perms">Additional permissions</Label>
              <Input
                id="g-perms"
                className="font-mono"
                placeholder="reports:read, exports:create"
                value={draft.permissions}
                onChange={(e) => setDraft((d) => ({ ...d, permissions: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Added only for this person.</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="g-exp">
              Expires
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">optional</span>
            </Label>
            <Input
              id="g-exp"
              type="date"
              min={today}
              className="sm:max-w-[200px]"
              value={draft.expires}
              aria-invalid={!!errors.expires}
              onChange={(e) => setDraft((d) => ({ ...d, expires: e.target.value }))}
            />
            <FieldError>{errors.expires}</FieldError>
            <p className="text-xs text-muted-foreground">Leave empty for access that doesn’t expire.</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : editing ? 'Save access' : 'Grant access'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
